
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, User as UserIcon, Loader2, Sparkles, Plus, MessageSquare, Trash2 } from 'lucide-react';
import { ChatMessage, Article, User, ChatSession } from '@/types';
import { 
  subscribeToChatSessions, 
  subscribeToSessionMessages, 
  createChatSession, 
  saveSessionMessages,
  deleteChatSession
} from '@/services/database';
import { generateWikiResponse } from '@/services/claudeService';

interface ChatInterfaceProps {
  articles: Article[];
  currentUser: User;
}

const formatMessage = (text: string) => {
  if (!text) return '';
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-black text-slate-900">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ articles, currentUser }) => {
  const firstName = currentUser.fullName.split(' ')[0];

  const welcomeMessage: ChatMessage = {
      id: 'welcome',
      role: 'model',
      text: `Olá, **${firstName}**! Eu sou a **Soes**, sua assistente da Wiki.\n\nPosso ler todos os artigos aprovados e tirar suas dúvidas sobre procedimentos.\n\nComo posso ajudar hoje?`,
      timestamp: Date.now()
  };

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // NEW: Ref to track if user explicitly requested a new chat
  const explicitlyRequestedNewChatRef = useRef(false);

  // Effect to load sessions for the current user
  useEffect(() => {
    if (!currentUser.id) {
      setSessions([]);
      setCurrentSessionId(null);
      setMessages([]);
      explicitlyRequestedNewChatRef.current = false; // Reset if user changes or logs out
      return;
    }

    console.log("ChatInterface (DEBUG): Subscribing to chat sessions for user ID:", currentUser.id);
    const unsubscribe = subscribeToChatSessions(currentUser.id, (loadedSessions) => {
      console.log(`ChatInterface (DEBUG): Loaded ${loadedSessions.length} sessions for user ${currentUser.id}.`);
      setSessions(loadedSessions);

      // Scenario 1: No current session selected, and user DID NOT explicitly request a new chat
      // This happens on initial load or if a previous session was deleted/invalidated
      if (!currentSessionId && loadedSessions.length > 0 && !explicitlyRequestedNewChatRef.current) {
        setCurrentSessionId(loadedSessions[0].id); // Default to the latest session
        console.log(`ChatInterface (DEBUG): Auto-selecting first session: ${loadedSessions[0].id}`);
      } 
      // Scenario 2: Current selected session no longer exists (e.g., deleted by admin or another device)
      else if (currentSessionId && !loadedSessions.some(s => s.id === currentSessionId)) {
        setCurrentSessionId(null); // Reset to null
        explicitlyRequestedNewChatRef.current = true; // Treat this as if a new chat was requested, show welcome.
        console.warn(`ChatInterface (DEBUG): Selected session ${currentSessionId} not found, resetting.`);
      }
      // If `currentSessionId` is already set (valid) or `explicitlyRequestedNewChatRef.current` is true, do nothing here.
      // `explicitlyRequestedNewChatRef.current` being true means the user WANTS null.
    });
    return () => {
      console.log("ChatInterface (DEBUG): Unsubscribing from chat sessions for user ID:", currentUser.id);
      unsubscribe();
    };
  }, [currentUser.id, currentSessionId]); // currentSessionId is a dependency to re-evaluate if it changes.

  // Effect to load messages for the selected session
  useEffect(() => {
    if (!currentSessionId) {
      console.log("ChatInterface (DEBUG): currentSessionId is null, messages should be empty or welcome.");
      setMessages([]); // Clears existing messages for new chat UI
      return;
    }
    console.log(`ChatInterface (DEBUG): Subscribing to messages for current session ID: ${currentSessionId}`);
    // Once a session is selected, clear the new chat request flag
    explicitlyRequestedNewChatRef.current = false; 
    const unsubscribe = subscribeToSessionMessages(currentSessionId, (session) => {
      if (session && session.messages) {
        setMessages(session.messages);
      } else {
        console.warn(`ChatInterface (DEBUG): Active session ${currentSessionId} not found or deleted, resetting currentSessionId to null.`);
        setCurrentSessionId(null); // This will trigger the previous useEffect.
        explicitlyRequestedNewChatRef.current = true; // Signal new chat intent
      }
    });
    return () => {
      console.log(`ChatInterface (DEBUG): Unsubscribing from session messages for ID: ${currentSessionId}`);
      unsubscribe();
    };
  }, [currentSessionId]); // currentSessionId is a dependency.

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const textToSend = input;
    setInput('');
    setIsLoading(true);
    setError(null);

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: textToSend,
      timestamp: Date.now()
    };

    let activeSessionId = currentSessionId;
    let messagesToSave: ChatMessage[] = []; // This will hold the current conversation state for DB ops

    // If starting a brand new chat
    if (!activeSessionId) {
      console.log("ChatInterface (DEBUG): No active session, creating a new one with initial message.");
      activeSessionId = await createChatSession(currentUser.id, textToSend);
      setCurrentSessionId(activeSessionId); // Update the state
      explicitlyRequestedNewChatRef.current = false; // A session is now active, clear flag
      messagesToSave = [userMsg]; // New session starts with this message
    } else {
      messagesToSave = [...messages, userMsg]; // Append to existing messages
    }
    setMessages(messagesToSave); // Optimistically update UI
    
    try {
      if (activeSessionId) {
        await saveSessionMessages(activeSessionId, messagesToSave);
        console.log("ChatInterface (DEBUG): User message saved to DB for session:", activeSessionId);
      } else {
          throw new Error("Sessão não foi criada ou identificada corretamente.");
      }

      const historyForAI = messagesToSave // Use `messagesToSave` which now includes the latest user message
          .filter(m => m.id !== 'welcome') // Ensure welcome message is never sent to AI
          .slice(-6) // Limit history to last 6 messages
          .map(m => ({
              role: m.role,
              parts: [{ text: m.text }]
          }));

      // NEW: Check for Office attachments in context articles
      let hasOfficeAttachments = false;
      for (const article of articles) {
          if (article.attachments) {
              for (const att of article.attachments) {
                  if (att.type.includes('wordprocessingml.document') || att.type.includes('msword') || 
                      att.type.includes('spreadsheetml.sheet') || att.type.includes('ms-excel')) {
                      hasOfficeAttachments = true;
                      break;
                  }
              }
          }
          if (hasOfficeAttachments) break;
      }
      
      const aiResponse = await generateWikiResponse(textToSend, historyForAI as any, articles, hasOfficeAttachments, currentUser.id);
      
      const modelMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: aiResponse,
        timestamp: Date.now()
      };

      const finalMessages = [...messagesToSave, modelMsg];
      await saveSessionMessages(activeSessionId, finalMessages);
      setMessages(finalMessages); // Update UI with AI response
      console.log("ChatInterface (DEBUG): Model response saved to DB for session:", activeSessionId);

    } catch (error: any) {
      console.error("ChatInterface (DEBUG): Error during chat interaction:", error);
      setError("⚠️ **Ops!** Tive um problema interno. Verifique o console se o erro persistir. " + error.message);
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(),
        role: 'model',
        text: "⚠️ **Ops!** Tive um problema interno. Verifique o console se o erro persistir.",
        timestamp: Date.now()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('Tem certeza que deseja excluir esta conversa?')) {
      try {
        console.log("ChatInterface (DEBUG): Deleting session:", sessionId);
        await deleteChatSession(sessionId);
        if (currentSessionId === sessionId) {
          setCurrentSessionId(null); // This will trigger useEffect, which will then set `explicitlyRequestedNewChatRef.current = true;` (from "Scenario 2")
          // Set to null directly to ensure it propagates, the effect will handle the new chat flag.
          console.log("ChatInterface (DEBUG): Deleted active session, setting currentSessionId to null.");
        }
      } catch (error: any) {
        console.error("ChatInterface (DEBUG): Erro ao excluir conversa:", error);
        setError("Erro ao excluir conversa: " + error.message);
      }
    }
  };

  const handleNewChatButtonClick = () => {
    setCurrentSessionId(null); // Clear current session
    setMessages([]); // Clear messages to show welcome message
    explicitlyRequestedNewChatRef.current = true; // Set flag to prevent auto-selection
    console.log("ChatInterface (DEBUG): 'New Chat' button clicked, explicitly requested new chat.");
  };

  return (
    <div className="flex h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
      {/* Sidebar */}
      <div className="hidden md:flex flex-col w-72 bg-slate-900 border-r border-slate-800">
        <div className="p-4 border-b border-slate-800">
          <button onClick={handleNewChatButtonClick} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold transition-all shadow-lg">
            <Plus size={18} /> Nova Conversa
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.map(s => (
            <div 
              key={s.id} 
              onClick={() => setCurrentSessionId(s.id)}
              className={`group w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer ${currentSessionId === s.id ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800/50'}`}
            >
              <MessageSquare size={16} className="flex-shrink-0" />
              <span className="text-sm font-medium truncate flex-1">{s.title}</span>
              <button 
                onClick={(e) => handleDeleteSession(e, s.id)}
                className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-all"
                title="Excluir conversa"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative">
        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 bg-slate-50/30">
          {/* Render welcome message only when currentSessionId is null AND there are no messages */}
          {currentSessionId === null && messages.length === 0 && (
            <div className="flex justify-start animate-fade-in">
              <div className="max-w-[85%] rounded-[32px] p-6 shadow-sm border bg-white border-slate-200 text-slate-800 rounded-bl-none">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-xl flex-shrink-0 bg-blue-50">
                    <Sparkles size={18} className="text-blue-600" />
                  </div>
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap">
                    {formatMessage(welcomeMessage.text)}
                  </div>
                </div>
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
              <div className={`max-w-[85%] rounded-[32px] p-6 shadow-sm border ${msg.role === 'user' ? 'bg-blue-600 text-white border-blue-500 rounded-br-none' : 'bg-white border-slate-200 text-slate-800 rounded-bl-none'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-xl flex-shrink-0 ${msg.role === 'user' ? 'bg-blue-500' : 'bg-blue-50'}`}>
                    {msg.role === 'user' ? <UserIcon size={18} /> : <Sparkles size={18} className="text-blue-600" />}
                  </div>
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap">
                    {formatMessage(msg.text)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-[32px] rounded-bl-none p-6 shadow-sm flex items-center gap-4 animate-pulse">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Soes está pensando...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-6 bg-white border-t border-slate-100">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <input 
              type="text" 
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Digite sua dúvida sobre a SÓES..."
              className="flex-1 px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
              disabled={isLoading}
            />
            <button 
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="p-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 disabled:opacity-30 transition-all shadow-xl active:scale-95"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};